import React, { useState, useCallback, useEffect } from 'react';
import { ActionButton } from '@pushpay/button';
import Dropdown from '@pushpay/dropdown';
import { DownloadIcon, CleanUpIcon, CrossIcon, LoadingOvalIcon } from '@pushpay/iconography';
import { EmptyTableIllustration } from '@pushpay/illustration';
import { clsx, createUseStyles } from '@pushpay/styles';
import { GivingOldTheme } from '@pushpay/theming';
import Tooltip from '@pushpay/tooltip';
import { useIsMounted } from '@pushpay/utils';
import Badge from './badge';
import { useDownloadsPanelContext } from './context/downloads-panel-context';
import Job from './job';
import { defaultDownloadsTranslate } from './translation/default-translate';
const downloadButtonSize = '18px';
const dismissAllButtonSize = '1.5em';
const useStyles = createUseStyles(theme => {
  const isGivingOld = GivingOldTheme.colors['product-color'] === theme.colors['product-color']; // back compatibility for giving old(red)

  return {
    root: {},
    button: {},
    downloadIcon: {
      fontSize: '1.5em'
    },
    headerBtnWrapRoot: {
      marginRight: theme.SPACING.GUTTER,
      height: dismissAllButtonSize,
      width: dismissAllButtonSize,
      '& > svg': {
        width: downloadButtonSize,
        height: downloadButtonSize
      }
    },
    jobsFlyoutRoot: {
      display: 'flex',
      flexDirection: 'column',
      width: '520px',
      maxWidth: '520px',
      height: '300px',
      backgroundColor: theme.colors['color-background-surface'],
      color: theme.colors['color-text-default']
    },
    flyoutHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '40px',
      padding: `${theme.SPACING.GUTTER} ${theme.SPACING.GUTTER} ${theme.SPACING.GUTTER} ${theme.SPACING.MEDIUM}`,
      marginBottom: theme.SPACING.GUTTER
    },
    flyoutHeaderDownloadTitle: {
      display: 'flex',
      alignItems: 'center'
    },
    flyoutHeaderActions: {
      display: 'flex'
    },
    flyoutMain: {
      display: 'flex',
      height: '100%',
      flexDirection: 'column',
      overflow: 'auto'
    },
    emptyContentWrap: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      width: '100%'
    },
    emptyContentTextWrap: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    emptyContentIcon: {
      fontSize: '120px',
      marginBottom: theme.SPACING.GUTTER
    },
    divider: {
      width: '1px',
      borderLeft: `1px solid ${theme.colors['palette-grey-dark-300']}`,
      marginRight: theme.SPACING.GUTTER
    },
    shake: {
      animation: '$shake 1s cubic-bezier(.36,.07,.19,.97) both infinite',
      transform: 'translate3d(0, 0, 0)'
    },
    '@keyframes shake': {
      '10%, 90%': {
        transform: 'translate3d(-1px, 0, 0)',
        backgroundColor: isGivingOld ? theme.colors['palette-white'] : theme.colors['palette-grey-dark-100'],
        color: isGivingOld ? theme.colors['palette-red-dark-200'] : theme.colors['color-text-inverse']
      },
      '20%, 80%': {
        transform: 'translate3d(1px, 0, 0)'
      },
      '30%, 50%, 70%': {
        transform: 'translate3d(-1.5px, 0, 0)',
        backgroundColor: isGivingOld ? theme.colors['palette-white'] : theme.colors['palette-grey-dark-100'],
        color: isGivingOld ? theme.colors['palette-red-dark-200'] : theme.colors['color-text-inverse']
      },
      '40%, 60%': {
        transform: 'translate3d(1.5px, 0, 0)'
      }
    },
    btnLoadingSpinner: {
      marginLeft: theme.SPACING.GUTTER
    },
    dismissAllTooltip: {
      zIndex: theme.ELEVATION.LAYOUT.Z_INDEX
    }
  };
});
let shakeTimeoutId;

const DownloadsPanel = props => {
  const {
    classes: classesProp,
    className: classNameProp,
    'data-pp-at-target': targetId,
    jobs,
    jobsNotificationsCount = 0,
    downloadUrl,
    dispatch,
    onClose,
    translate = defaultDownloadsTranslate
  } = props;
  const classes = useStyles(classesProp);
  const className = clsx(classNameProp, classes.root);
  const [isDismissingAll, setIsDismissingAll] = useState(false);
  const [isShakingBadge, setIsShakingBadge] = useState(false);
  const checkIsMounted = useIsMounted();
  const {
    triggerButtonRef,
    isRefetching
  } = useDownloadsPanelContext();
  const onDismissAll = useCallback(() => {
    setIsDismissingAll(true);
    dispatch({
      type: 'DISMISS_ALL'
    });
  }, [dispatch]);
  useEffect(function completeDismissingAll() {
    if (isDismissingAll) {
      setIsDismissingAll(false);
    }
  }, [isDismissingAll, jobs]);

  const renderOverlay = close => /*#__PURE__*/React.createElement("div", {
    className: classes.jobsFlyoutRoot
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.flyoutHeader
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.flyoutHeaderDownloadTitle
  }, /*#__PURE__*/React.createElement("b", null, "Downloads"), isRefetching && /*#__PURE__*/React.createElement(LoadingOvalIcon, {
    className: classes.btnLoadingSpinner,
    "data-pp-at-target": `${targetId}-loading-icon`
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.flyoutHeaderActions
  }, !!jobs.size && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Tooltip, {
    classes: {
      tooltip: classes.dismissAllTooltip
    },
    content: translate('downloadsPanel.dismissAll'),
    "data-pp-at-target": `${targetId}-dismiss-all-tooltip`,
    placement: "bottom"
  }, /*#__PURE__*/React.createElement(ActionButton, {
    displayStyle: "secondary",
    className: classes.headerBtnWrapRoot,
    icon: isDismissingAll ? LoadingOvalIcon : CleanUpIcon,
    onClick: onDismissAll,
    disabled: isDismissingAll,
    "data-pp-at-target": `${targetId}-dismiss-all-button`
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.divider
  })), /*#__PURE__*/React.createElement(Tooltip, {
    classes: {
      tooltip: classes.dismissAllTooltip
    },
    content: translate('downloadsPanel.close'),
    "data-pp-at-target": `${targetId}-close-tooltip`,
    placement: "bottom"
  }, /*#__PURE__*/React.createElement(ActionButton, {
    displayStyle: "secondary",
    className: classes.headerBtnWrapRoot,
    icon: CrossIcon,
    onClick: () => {
      close();
      if (onClose) onClose();
    },
    "data-pp-at-target": `${targetId}-flyout-close-button`
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.flyoutMain
  }, !jobs.size && /*#__PURE__*/React.createElement("div", {
    className: classes.emptyContentWrap
  }, /*#__PURE__*/React.createElement(EmptyTableIllustration, {
    className: classes.emptyContentIcon,
    "data-pp-at-target": `${targetId}-empty-illustration`
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.emptyContentTextWrap
  }, /*#__PURE__*/React.createElement("b", null, translate('downloadsPanel.keepTrackOfYourExports')), /*#__PURE__*/React.createElement("div", null, translate('downloadsPanel.emptyExportsText')))), [...jobs.entries()].map(([jobKey, job]) => /*#__PURE__*/React.createElement(Job, {
    key: jobKey,
    job: job,
    downloadUrl: (downloadUrl === null || downloadUrl === void 0 ? void 0 : downloadUrl.jobKey) === jobKey ? downloadUrl.url : undefined,
    dispatch: dispatch,
    translate: translate,
    "data-pp-at-target": `${targetId}-${jobKey}`
  }))));

  useEffect(() => {
    if (jobsNotificationsCount) {
      setIsShakingBadge(true);
      if (shakeTimeoutId) clearTimeout(shakeTimeoutId);
      shakeTimeoutId = setTimeout(() => {
        if (checkIsMounted()) {
          setIsShakingBadge(false);
        }
      }, 2000);
    }
  }, [jobsNotificationsCount]);
  return /*#__PURE__*/React.createElement(Dropdown, {
    ref: triggerButtonRef,
    className: className,
    classes: {
      summary: classes.button
    },
    "data-pp-at-target": `${targetId}-downloads-panel-dropdown`,
    alignment: "right",
    hideDropdownArrow: true,
    onClickAway: onClose,
    overlay: renderOverlay
  }, /*#__PURE__*/React.createElement(Badge, {
    classes: {
      badge: isShakingBadge ? classes.shake : ''
    },
    "data-pp-at-target": `${targetId}-jobs-badge`,
    count: jobsNotificationsCount
  }, /*#__PURE__*/React.createElement(DownloadIcon, {
    className: classes.downloadIcon,
    "data-pp-at-target": `${targetId}-download-icon`
  })));
};

export default DownloadsPanel;